import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { sourcesApi, type Source } from '@/api/sourcesApi'
import type {
  IntegrationCategory,
  IntegrationDirection,
  TransportProtocol,
} from '@/api/integrationsApi'

const CATEGORIES: IntegrationCategory[] = [
  'instrument',
  'ehr',
  'lis',
  'printer',
  'immunoassay',
  'cell_counter',
  'other',
]
const PROTOCOLS: TransportProtocol[] = ['hl7', 'astm', 'fhir', 'csv', 'json', 'api', 's3']
const DIRECTIONS: IntegrationDirection[] = ['inbound', 'outbound', 'bidirectional']

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** When provided the modal edits that source; otherwise it creates a new one. */
  source?: Source | null
  onSaved: (source: Source) => void
}

export function SourceFormModal({ open, onOpenChange, source, onSaved }: Props) {
  const queryClient = useQueryClient()
  const isEdit = !!source

  const [name, setName] = useState('')
  const [key, setKey] = useState('')
  const [keyEdited, setKeyEdited] = useState(false)
  const [category, setCategory] = useState<IntegrationCategory>('instrument')
  const [protocol, setProtocol] = useState<TransportProtocol | ''>('')
  const [direction, setDirection] = useState<IntegrationDirection>('inbound')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Sync form fields whenever the dialog opens (prefill in edit mode, clear in create).
  useEffect(() => {
    if (!open) return
    if (source) {
      setName(source.name)
      setKey(source.key)
      setKeyEdited(true)
      setCategory((source.category as IntegrationCategory) ?? 'instrument')
      setProtocol((source.defaultProtocol as TransportProtocol | null) ?? '')
      setDirection((source.defaultDirection as IntegrationDirection) ?? 'inbound')
    } else {
      setName('')
      setKey('')
      setKeyEdited(false)
      setCategory('instrument')
      setProtocol('')
      setDirection('inbound')
    }
    setError(null)
  }, [open, source])

  // Auto-derive the key from the name (create mode only) until edited manually.
  useEffect(() => {
    if (!isEdit && !keyEdited) setKey(slugify(name))
  }, [name, keyEdited, isEdit])

  async function handleSave() {
    if (!name.trim() || (!isEdit && !key.trim())) return
    setSaving(true)
    setError(null)
    try {
      const saved =
        isEdit && source
          ? await sourcesApi.update(source.id, {
              name: name.trim(),
              category,
              defaultProtocol: protocol || undefined,
              defaultDirection: direction,
            })
          : await sourcesApi.create({
              name: name.trim(),
              key: key.trim(),
              category,
              defaultProtocol: protocol || undefined,
              defaultDirection: direction,
            })
      await queryClient.invalidateQueries({ queryKey: ['sources'] })
      onSaved(saved)
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save source.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (saving) return
        onOpenChange(o)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Source' : 'New Source'}</DialogTitle>
          <DialogDescription>
            A source is a type of system/device (e.g. Sysmex XP-300) that integrations are built
            from.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="source-name">Name</Label>
            <Input
              id="source-name"
              placeholder="e.g. Sysmex XP-300"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="source-key">Key</Label>
            <Input
              id="source-key"
              placeholder="e.g. sysmex_xp300"
              value={key}
              disabled={isEdit}
              onChange={(e) => {
                setKeyEdited(true)
                setKey(e.target.value)
              }}
            />
            <p className="text-xs text-muted-foreground">
              {isEdit ? "Unique slug. Can't be changed." : 'Unique slug. Auto-filled from the name.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Category</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as IntegrationCategory)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} className="capitalize">
                      {c.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-1.5">
              <Label>Default direction</Label>
              <Select value={direction} onValueChange={(v) => setDirection(v as IntegrationDirection)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIRECTIONS.map((d) => (
                    <SelectItem key={d} value={d} className="capitalize">
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Default protocol</Label>
            <Select value={protocol || undefined} onValueChange={(v) => setProtocol(v as TransportProtocol)}>
              <SelectTrigger>
                <SelectValue placeholder="Select protocol (optional)" />
              </SelectTrigger>
              <SelectContent>
                {PROTOCOLS.map((p) => (
                  <SelectItem key={p} value={p} className="uppercase">
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!name.trim() || (!isEdit && !key.trim()) || saving}>
            {saving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Source'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
